import type { fetchProducts, FetchProductQuery } from '@eliadmin/core/src/api/products/product-api';
import { buildProductsQuery } from '@eliadmin/core/src/utils/product-utils';

export const CATEGORY_PAGE_SIZE = 10;
export type CategoryProductsPage = Awaited<ReturnType<typeof fetchProducts>>;

export type CategoryFirstPage = {
  audience: 'guest';
  categoryId: number;
  locale: string;
  queryIdentity: string;
  fetchedAt: number;
  page: CategoryProductsPage;
};

export function buildCategoryFirstPageQuery(categoryId: number) {
  return buildProductsQuery({ categories: [categoryId], results: CATEGORY_PAGE_SIZE });
}

export function canPrefetchCategoryFirstPage(
  authSlugDetail?: boolean,
  searchParams?: Record<string, string | string[] | undefined>,
) {
  return authSlugDetail !== true && Object.keys(searchParams ?? {}).length === 0;
}

export function createCategoryFirstPage(
  categoryId: number,
  locale: string,
  page: CategoryProductsPage,
  fetchedAt: number,
): CategoryFirstPage | undefined {
  if (
    page.meta.current_page !== 1 ||
    page.meta.per_page !== CATEGORY_PAGE_SIZE ||
    page.data.length > CATEGORY_PAGE_SIZE
  ) {
    return undefined;
  }

  return {
    audience: 'guest',
    categoryId,
    locale,
    queryIdentity: JSON.stringify(buildCategoryFirstPageQuery(categoryId)),
    fetchedAt,
    page,
  };
}

export function getCategoryFirstPageSeed(
  seed: CategoryFirstPage | undefined,
  context: {
    categoryId: number;
    locale: string;
    isAuthenticated: boolean;
    query: FetchProductQuery;
    hasAttributes: boolean;
    // Undefined only before the nested client URL observer has hydrated.
    search?: string;
  },
) {
  if (
    !seed ||
    seed.audience !== 'guest' ||
    context.isAuthenticated ||
    seed.categoryId !== context.categoryId ||
    seed.locale !== context.locale ||
    context.hasAttributes ||
    seed.queryIdentity !== JSON.stringify(context.query)
  ) {
    return undefined;
  }

  const query = new URLSearchParams(context.search);
  // A cached default page must never seed page 2, a filter, sort or variant URL,
  // even before CategoryParamsSync has copied that URL into the filter store.
  if (
    query.getAll('page').length > 1 ||
    [...query].some(([key, value]) => key !== 'page' || value !== '1')
  ) {
    return undefined;
  }

  return seed;
}
